import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronsDown, Triangle, ArrowRight, PhoneCall, Globe2 } from "lucide-react";
import { fetchNavData } from "@/components/navData";

const ARROW_ICONS = { chevron: ChevronDown, double: ChevronsDown };

export default function StudyAbroadMenu({ onCallback, label = "Study Abroad", link = "/courses", showArrow = true, arrowStyle = "chevron" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const closeTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { fetchNavData().then(setData); }, []);

  const enter = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const leave = () => { closeTimer.current = setTimeout(() => setOpen(false), 180); };

  const countries = data?.study_abroad?.countries || [];
  const current = countries[active] || countries[0];
  const isExternal = (u) => /^https?:\/\//i.test(u || "");
  const go = (url) => {
    setOpen(false);
    if (isExternal(url)) window.open(url, "_blank", "noopener,noreferrer");
    else navigate(url || link);
  };

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave} data-testid="study-abroad-menu">
      <button onClick={() => go(link)} data-testid="study-abroad-trigger"
        className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${open ? "text-rose-600" : "text-slate-700 hover:text-rose-600"}`}>
        {label} {showArrow && (arrowStyle === "caret"
          ? <Triangle className={`w-2.5 h-2.5 fill-current transition-transform ${open ? "" : "rotate-180"}`} data-testid="study-abroad-arrow" />
          : (() => { const A = ARROW_ICONS[arrowStyle] || ChevronDown; return <A className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} data-testid="study-abroad-arrow" />; })())}
      </button>

      {open && countries.length > 0 && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50" data-testid="study-abroad-panel">
          <div className="w-[820px] max-w-[92vw] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden grid grid-cols-12">
            <div className="col-span-4 bg-slate-50 border-r border-slate-100 py-4 max-h-[440px] overflow-y-auto">
              {countries.map((c, i) => (
                <button key={c.country || c.label} onMouseEnter={() => setActive(i)} onClick={() => go(c.link)} data-testid={`sa-country-${(c.country || c.label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${i === active ? "bg-white text-rose-600 border-r-2 border-rose-600" : "text-slate-700 hover:bg-white"}`}>
                  <Globe2 className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-semibold flex-1">{c.label}</span>
                  <ChevronDown className="w-3.5 h-3.5 -rotate-90 text-slate-300" />
                </button>
              ))}
              <button onClick={() => go(link)} data-testid="sa-all-destinations"
                className="flex items-center gap-1.5 px-5 py-3 text-sm font-bold text-rose-600 hover:gap-2.5 transition-all text-left">
                All destinations <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="col-span-5 p-6">
              {current && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest text-rose-600">{current.label}</p>
                  <p className="text-sm text-slate-500 mt-1">Top programs students are shortlisting</p>
                  <div className="mt-4 flex flex-col gap-3">
                    {(current.links || []).length > 0 ? (
                      current.links.map((l, li) => (
                        isExternal(l.url) ? (
                          <a key={`ml-${li}`} href={l.url} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid={`sa-manual-link-${li}`} className="group">
                            <p className="text-sm font-bold text-slate-800 group-hover:text-rose-600 transition-colors leading-snug">{l.title}</p>
                          </a>
                        ) : (
                          <Link key={`ml-${li}`} to={l.url} onClick={() => setOpen(false)} data-testid={`sa-manual-link-${li}`} className="group">
                            <p className="text-sm font-bold text-slate-800 group-hover:text-rose-600 transition-colors leading-snug">{l.title}</p>
                          </Link>
                        )
                      ))
                    ) : (
                      <>
                        {current.programs.map((p) => (
                          <Link key={p.slug} to={p.url_path || `/courses/${p.slug}`} onClick={() => setOpen(false)} data-testid={`sa-program-${p.slug}`} className="group">
                            <p className="text-sm font-bold text-slate-800 group-hover:text-rose-600 transition-colors leading-snug">{p.title}</p>
                            <p className="text-xs text-slate-400">{p.university}</p>
                          </Link>
                        ))}
                        {current.programs.length === 0 && <p className="text-sm text-slate-400">Programs for this destination are being finalized.</p>}
                      </>
                    )}
                  </div>
                  {isExternal(current.link) ? (
                    <a href={current.link} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid="sa-view-country"
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-rose-600 mt-5 hover:gap-2.5 transition-all">
                      {current.view_all_label || `View all ${current.country} programs`} <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <Link to={current.link} onClick={() => setOpen(false)} data-testid="sa-view-country"
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-rose-600 mt-5 hover:gap-2.5 transition-all">
                      {current.view_all_label || `View all ${current.country} programs`} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </>
              )}
            </div>

            <div className="col-span-3 bg-gradient-to-b from-rose-50 to-orange-50 p-6 border-l border-rose-100">
              <p className="text-sm font-bold text-slate-800">Confused about which country?</p>
              <p className="text-xs text-slate-500 mt-1 mb-3">Free 1:1 counseling call — budget, visa & university fit.</p>
              <button onClick={() => { setOpen(false); onCallback?.(); }} data-testid="sa-counselor-cta"
                className="w-full flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors">
                <PhoneCall className="w-4 h-4" /> Talk to a counselor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
